import './CharacterMenu.css';
import { MAX_NAME_LENGTH, sanitizeName } from '../net/protocol';
import {
  BEARDS_LIST,
  EYEBROWS_LIST,
  HAIR_COLORS,
  HAIRSTYLES_LIST,
  OUTFITS_LIST,
  type BeardId,
  type CharacterConfig,
  type EyebrowsId,
  type Gender,
  type HairColor,
  type HairstyleId,
  type OutfitId,
  type SkinTone,
} from '../character/CharacterDefinition';

export interface CharacterMenuCallbacks {
  /**
   * Vrácený Promise se čeká — dokud běží, panel je zamčený a ukazuje
   * probíhající práci. Bez toho by rychlé klikání spustilo dvě výměny
   * dílu naráz a jedna by druhé přepsala výsledek.
   */
  onConfigChange: (config: CharacterConfig, changedProp: keyof CharacterConfig) => Promise<void> | void;
  onEnterWorld: (config: CharacterConfig) => void;
  /** Otočení náhledu o zadaný úhel v radiánech. */
  onRotatePreview?: (deltaRadians: number) => void;
  /** Zapnutí/vypnutí automatického otáčení náhledu. */
  onAutoRotate?: (enabled: boolean) => void;
}

type TabId = 'basics' | 'appearance' | 'gear';

interface TabDef {
  id: TabId;
  label: string;
}

const TABS: TabDef[] = [
  { id: 'basics', label: 'Základ' },
  { id: 'appearance', label: 'Vzhled' },
  { id: 'gear', label: 'Výstroj' },
];

const MIN_NAME_LENGTH = 3;

const SKIN_TONES: { id: SkinTone; name: string; hex: string }[] = [
  { id: 'light', name: 'Světlá', hex: '#e8c4a4' },
  { id: 'dark', name: 'Tmavá', hex: '#7a5540' },
];

/**
 * Tenké lineární ikony. Kreslené inline v `currentColor`, takže dědí barvu
 * stavu (neaktivní / vybráno) bez jediného dalšího souboru a bez emoji,
 * které se na každém systému vykreslí jinak.
 */
const ICONS = {
  male: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"><circle cx="10" cy="14" r="5.5"/><path d="M14.2 9.8 20 4m0 0h-5m5 0v5"/></svg>',
  female:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"><circle cx="12" cy="9" r="5.5"/><path d="M12 14.5V21M9 18.5h6"/></svg>',
  none: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"><circle cx="12" cy="12" r="8.5"/><path d="M6 18 18 6"/></svg>',
  tunic:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"><path d="M9 3 5 5.5 6.5 10 8 9.2V21h8V9.2l1.5.8L19 5.5 15 3l-3 2.2z"/></svg>',
  armor:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"><path d="M12 3 5 5.5v6c0 4.5 3 8 7 9.5 4-1.5 7-5 7-9.5v-6z"/><path d="M12 7.5v8"/></svg>',
  left: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M15 5 8 12l7 7"/></svg>',
  right:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="m9 5 7 7-7 7"/></svg>',
  autoRotate:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 12a8 8 0 1 1-2.7-6"/><path d="M20 4v5h-5"/></svg>',
} as const;

const OUTFIT_ICONS: Record<OutfitId, string> = {
  none: ICONS.none,
  peasant: ICONS.tunic,
  ranger: ICONS.armor,
};

/** Bezpečné vložení textu do HTML šablony. */
function esc(value: string): string {
  return value.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);
}

export class CharacterMenu {
  private readonly container: HTMLElement;
  private readonly config: CharacterConfig;
  private readonly callbacks: CharacterMenuCallbacks;
  private visible = true;
  private activeTab: TabId = 'basics';
  private busy = false;
  private autoRotate = false;

  constructor(parent: HTMLElement, initialConfig: CharacterConfig, callbacks: CharacterMenuCallbacks) {
    this.config = { ...initialConfig };
    this.callbacks = callbacks;

    this.container = document.createElement('div');
    this.container.id = 'char-menu';
    parent.appendChild(this.container);

    this.renderShell();
    this.renderBody();

    window.addEventListener('keydown', this.onKeyDown);
  }

  get isVisible(): boolean {
    return this.visible;
  }

  show(): void {
    this.visible = true;
    this.container.classList.remove('hidden');
  }

  hide(): void {
    this.visible = false;
    this.container.classList.add('hidden');
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    this.container.remove();
  }

  // --- vykreslení -------------------------------------------------------

  /** Části, které se při změně nastavení nepřekreslují. */
  private renderShell(): void {
    this.container.innerHTML = `
      <div class="cc-frame"></div>
      <div class="cc-vignette"></div>

      <div class="cc-title">
        <h1>Tvorba postavy</h1>
        <div class="cc-rule"></div>
        <p>Podoba, kterou si zvolíš, tě bude provázet celým světem.</p>
      </div>

      <div class="cc-preview-controls">
        <button class="cc-icon-btn" data-rotate="-1" type="button" title="Otočit doleva">${ICONS.left}</button>
        <button class="cc-icon-btn" data-rotate="1" type="button" title="Otočit doprava">${ICONS.right}</button>
        <button class="cc-icon-btn" id="cc-autorotate" type="button" aria-pressed="false" title="Automatické otáčení">${ICONS.autoRotate}</button>
        <span class="cc-preview-hint">Tažením otočíš a nakloníš · kolečkem přiblížíš</span>
      </div>

      <div class="cc-panel" id="cc-panel">
        <div class="cc-progress"><i></i></div>

        <div class="cc-tabs" role="tablist">
          ${TABS.map(
            (t) => `
            <button class="cc-tab" role="tab" type="button" data-tab="${t.id}"
                    aria-selected="${t.id === this.activeTab}">${esc(t.label)}</button>`,
          ).join('')}
        </div>

        <div class="cc-body" id="cc-body"></div>

        <div class="cc-footer">
          <button class="cc-btn-primary" id="cc-enter" type="button">Vstoupit do světa</button>
          <button class="cc-btn-ghost" id="cc-random" type="button">Náhodná podoba</button>
        </div>
      </div>
    `;

    this.container.querySelectorAll<HTMLButtonElement>('.cc-tab').forEach((tab) => {
      tab.onclick = () => {
        this.activeTab = tab.dataset.tab as TabId;
        this.container
          .querySelectorAll<HTMLButtonElement>('.cc-tab')
          .forEach((t) => t.setAttribute('aria-selected', String(t.dataset.tab === this.activeTab)));
        this.renderBody();
      };
    });

    this.container.querySelectorAll<HTMLButtonElement>('[data-rotate]').forEach((btn) => {
      btn.onclick = () => this.callbacks.onRotatePreview?.(Number(btn.dataset.rotate) * (Math.PI / 8));
    });

    const autoBtn = this.container.querySelector<HTMLButtonElement>('#cc-autorotate');
    if (autoBtn) {
      autoBtn.onclick = () => {
        this.autoRotate = !this.autoRotate;
        autoBtn.setAttribute('aria-pressed', String(this.autoRotate));
        this.callbacks.onAutoRotate?.(this.autoRotate);
      };
    }

    const enter = this.container.querySelector<HTMLButtonElement>('#cc-enter');
    if (enter) {
      enter.onclick = () => {
        if (this.busy || !this.nameIsValid) return;
        this.hide();
        this.callbacks.onEnterWorld(this.config);
      };
    }

    const random = this.container.querySelector<HTMLButtonElement>('#cc-random');
    if (random) random.onclick = () => void this.randomize();
  }

  private renderBody(): void {
    const body = this.container.querySelector<HTMLElement>('#cc-body');
    if (!body) return;

    body.innerHTML =
      this.activeTab === 'basics'
        ? this.basicsMarkup()
        : this.activeTab === 'appearance'
          ? this.appearanceMarkup()
          : this.gearMarkup();

    this.bindBody();
    body.scrollTop = 0;
  }

  private basicsMarkup(): string {
    return `
      <fieldset class="cc-field">
        <legend>Jméno postavy</legend>
        <div class="cc-input-wrap">
          <input class="cc-input" id="cc-name" type="text" autocomplete="off" spellcheck="false"
                 maxlength="${MAX_NAME_LENGTH}" placeholder="Zadej jméno"
                 value="${esc(this.config.name)}" />
          <span class="cc-input-count" id="cc-name-count">${this.config.name.length}/${MAX_NAME_LENGTH}</span>
        </div>
        <p class="cc-hint" id="cc-name-hint">Pod tímto jménem tě uvidí ostatní hráči.</p>
      </fieldset>

      <fieldset class="cc-field">
        <legend>Pohlaví</legend>
        <div class="cc-cards" data-cols="2">
          ${this.cardMarkup('gender', 'male', ICONS.male, 'Muž', '')}
          ${this.cardMarkup('gender', 'female', ICONS.female, 'Žena', '')}
        </div>
      </fieldset>

      <fieldset class="cc-field">
        <legend>Tón pleti</legend>
        <div class="cc-swatches">
          ${SKIN_TONES.map(
            (t) => `
            <button class="cc-swatch" type="button" data-skin="${t.id}"
                    aria-pressed="${this.config.skinTone === t.id}"
                    title="${esc(t.name)}" style="background:${t.hex}"></button>`,
          ).join('')}
        </div>
      </fieldset>
    `;
  }

  private appearanceMarkup(): string {
    const hairstyles = this.availableHairstyles();
    const eyebrows = this.availableEyebrows();
    const showBeard = this.config.gender === 'male';

    return `
      ${this.stepperMarkup('hairstyle', 'Účes', hairstyles, this.config.hairstyle)}

      <fieldset class="cc-field">
        <legend>Barva vlasů</legend>
        <div class="cc-swatches">
          ${HAIR_COLORS.map(
            (c) => `
            <button class="cc-swatch" type="button" data-haircolor="${c.id}"
                    aria-pressed="${this.config.hairColor === c.id}"
                    title="${esc(c.name)}"
                    style="background:#${c.hex.toString(16).padStart(6, '0')}"></button>`,
          ).join('')}
        </div>
      </fieldset>

      ${showBeard ? this.stepperMarkup('beard', 'Vousy', BEARDS_LIST, this.config.beard) : ''}
      ${this.stepperMarkup('eyebrows', 'Obočí', eyebrows, this.config.eyebrows)}
    `;
  }

  private gearMarkup(): string {
    const showHood = this.config.outfit === 'ranger';
    return `
      <fieldset class="cc-field">
        <legend>Výstroj</legend>
        <div class="cc-cards">
          ${OUTFITS_LIST.map((o) =>
            this.cardMarkup('outfit', o.id, OUTFIT_ICONS[o.id], o.name, o.desc),
          ).join('')}
        </div>
      </fieldset>

      ${
        showHood
          ? `<fieldset class="cc-field">
               <legend>Doplňky</legend>
               <label class="cc-switch">
                 <span>Nasazená kápě</span>
                 <input type="checkbox" id="cc-hood" ${this.config.hood ? 'checked' : ''} />
                 <span class="cc-switch-track"></span>
               </label>
             </fieldset>`
          : ''
      }
    `;
  }

  private cardMarkup(
    group: string,
    value: string,
    icon: string,
    name: string,
    desc: string,
  ): string {
    const current = this.config[group as 'gender' | 'outfit'] as string;
    return `
      <button class="cc-card" type="button" data-group="${group}" data-value="${value}"
              aria-pressed="${current === value}">
        ${icon}
        <span class="cc-card-text">
          <span class="cc-card-name">${esc(name)}</span>
          ${desc ? `<span class="cc-card-desc">${esc(desc)}</span>` : ''}
        </span>
      </button>
    `;
  }

  private stepperMarkup(
    group: string,
    label: string,
    options: { id: string; name: string }[],
    current: string,
  ): string {
    const index = Math.max(
      0,
      options.findIndex((o) => o.id === current),
    );
    const option = options[index];
    return `
      <fieldset class="cc-field">
        <legend>${esc(label)}</legend>
        <div class="cc-stepper" data-group="${group}">
          <button class="cc-step-btn" type="button" data-step="-1" ${index === 0 ? 'disabled' : ''}
                  title="Předchozí">${ICONS.left}</button>
          <div class="cc-step-value">
            <span class="cc-step-name">${esc(option?.name ?? '—')}</span>
            <span class="cc-step-count">${index + 1} / ${options.length}</span>
          </div>
          <button class="cc-step-btn" type="button" data-step="1"
                  ${index === options.length - 1 ? 'disabled' : ''} title="Další">${ICONS.right}</button>
        </div>
      </fieldset>
    `;
  }

  // --- události ---------------------------------------------------------

  private bindBody(): void {
    this.container.querySelectorAll<HTMLButtonElement>('.cc-card').forEach((card) => {
      card.onclick = () => {
        const group = card.dataset.group!;
        const value = card.dataset.value!;
        if (group === 'gender') void this.setGender(value as Gender);
        else if (group === 'outfit') void this.change('outfit', value as OutfitId);
      };
    });

    this.container.querySelectorAll<HTMLElement>('.cc-stepper').forEach((stepper) => {
      const group = stepper.dataset.group as 'hairstyle' | 'beard' | 'eyebrows';
      stepper.querySelectorAll<HTMLButtonElement>('.cc-step-btn').forEach((btn) => {
        btn.onclick = () => void this.step(group, Number(btn.dataset.step));
      });
    });

    this.container.querySelectorAll<HTMLButtonElement>('[data-skin]').forEach((btn) => {
      btn.onclick = () => void this.change('skinTone', btn.dataset.skin as SkinTone);
    });

    this.container.querySelectorAll<HTMLButtonElement>('[data-haircolor]').forEach((btn) => {
      btn.onclick = () => void this.change('hairColor', btn.dataset.haircolor as HairColor);
    });

    const hood = this.container.querySelector<HTMLInputElement>('#cc-hood');
    if (hood) hood.onchange = () => void this.change('hood', hood.checked);

    const nameInput = this.container.querySelector<HTMLInputElement>('#cc-name');
    if (nameInput) {
      // Záměrně bez překreslení panelu — to by po každém znaku sebralo
      // políčku kurzor. Stačí aktualizovat konfiguraci a stav tlačítka.
      nameInput.oninput = () => {
        this.config.name = sanitizeName(nameInput.value);
        const count = this.container.querySelector('#cc-name-count');
        if (count) count.textContent = `${this.config.name.length}/${MAX_NAME_LENGTH}`;
        this.updateEnterState();
      };
    }

    this.updateEnterState();
  }

  /** Bez jména se do světa nevstupuje — ostatní by viděli prázdnou jmenovku. */
  private get nameIsValid(): boolean {
    return this.config.name.trim().length >= MIN_NAME_LENGTH;
  }

  private updateEnterState(): void {
    const enter = this.container.querySelector<HTMLButtonElement>('#cc-enter');
    if (!enter) return;
    const ok = this.nameIsValid;
    enter.disabled = !ok;
    enter.textContent = ok ? 'Vstoupit do světa' : `Zadej jméno (min. ${MIN_NAME_LENGTH} znaky)`;
  }

  private onKeyDown = (event: KeyboardEvent): void => {
    if (!this.visible || this.busy) return;
    if (event.key === 'Enter') {
      if (!this.nameIsValid) {
        // Bez jména se nedá pokračovat — přepni na záložku, kde se zadává.
        if (this.activeTab !== 'basics') {
          this.activeTab = 'basics';
          this.container
            .querySelectorAll<HTMLButtonElement>('.cc-tab')
            .forEach((t) => t.setAttribute('aria-selected', String(t.dataset.tab === 'basics')));
          this.renderBody();
        }
        this.container.querySelector<HTMLInputElement>('#cc-name')?.focus();
        return;
      }
      event.preventDefault();
      this.hide();
      this.callbacks.onEnterWorld(this.config);
    }
  };

  // --- změny nastavení --------------------------------------------------

  private availableHairstyles(): { id: HairstyleId; name: string }[] {
    return HAIRSTYLES_LIST.filter((h) => !h.forGender || h.forGender === this.config.gender);
  }

  private availableEyebrows(): { id: EyebrowsId; name: string }[] {
    return EYEBROWS_LIST.filter((e) => !e.forGender || e.forGender === this.config.gender);
  }

  /**
   * Jediné místo, kde se mění konfigurace. Drží zámek po dobu, kdy se
   * dohrává model, a teprve pak překreslí panel — takže se stav v UI
   * nikdy nerozejde se stavem modelu.
   */
  private async change<K extends keyof CharacterConfig>(
    key: K,
    value: CharacterConfig[K],
  ): Promise<void> {
    if (this.busy || this.config[key] === value) return;
    this.config[key] = value;
    this.renderBody();
    await this.runLocked(key);
  }

  private async runLocked(key: keyof CharacterConfig): Promise<void> {
    const panel = this.container.querySelector<HTMLElement>('#cc-panel');
    this.busy = true;
    panel?.classList.add('busy');
    try {
      await this.callbacks.onConfigChange(this.config, key);
    } finally {
      this.busy = false;
      panel?.classList.remove('busy');
    }
  }

  private async step(group: 'hairstyle' | 'beard' | 'eyebrows', delta: number): Promise<void> {
    if (this.busy) return;
    const options: { id: string }[] =
      group === 'hairstyle'
        ? this.availableHairstyles()
        : group === 'beard'
          ? BEARDS_LIST
          : this.availableEyebrows();

    const index = options.findIndex((o) => o.id === this.config[group]);
    const next = Math.min(options.length - 1, Math.max(0, index + delta));
    if (next === index) return;

    const id = options[next].id;
    if (group === 'hairstyle') this.config.hairstyle = id as HairstyleId;
    else if (group === 'beard') this.config.beard = id as BeardId;
    else this.config.eyebrows = id as EyebrowsId;

    this.renderBody();
    await this.runLocked(group);
  }

  /**
   * Změna pohlaví mění i nabídku účesů a obočí, takže se současně
   * překlopí na výchozí hodnoty platné pro nové pohlaví.
   */
  private async setGender(gender: Gender): Promise<void> {
    if (this.busy || this.config.gender === gender) return;
    this.config.gender = gender;
    if (gender === 'female') {
      this.config.hairstyle = 'Hair_Buns';
      this.config.eyebrows = 'Eyebrows_Female';
      this.config.beard = 'none';
    } else {
      this.config.hairstyle = 'Hair_SimpleParted';
      this.config.eyebrows = 'Eyebrows_Regular';
    }
    this.renderBody();
    await this.runLocked('gender');
  }

  private async randomize(): Promise<void> {
    if (this.busy) return;
    const pick = <T>(items: T[]): T => items[Math.floor(Math.random() * items.length)];

    this.config.gender = pick<Gender>(['male', 'female']);
    this.config.hairstyle = pick(this.availableHairstyles()).id;
    this.config.eyebrows = pick(this.availableEyebrows()).id;
    this.config.beard = this.config.gender === 'male' ? pick(BEARDS_LIST).id : 'none';
    this.config.hairColor = pick(HAIR_COLORS).id;
    this.config.skinTone = pick(SKIN_TONES).id;
    this.config.outfit = pick(OUTFITS_LIST).id;
    this.config.hood = this.config.outfit === 'ranger' && Math.random() < 0.4;

    this.renderBody();
    // Postava se překládá celá — jednotlivé díly by znamenaly šest
    // po sobě jdoucích načtení a šest překreslení panelu.
    await this.runLocked('gender');
  }
}
